import { useState, useEffect, useCallback } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import { auth, db, requestNotificationToken, onForegroundMessage } from "../firebase";

export function useAuth() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Listen to auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const profileSnap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (profileSnap.exists()) {
          setUserProfile({ uid: firebaseUser.uid, ...profileSnap.data() });
        }
        // If permission already granted, refresh token on every app open
        // (tokens expire/rotate — this keeps Firestore up to date)
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          registerFCMToken(firebaseUser.uid);
        } else {
          // On non-iOS, we can auto-prompt for permission
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          if (!isIOS && Notification.permission !== "denied") {
            registerFCMToken(firebaseUser.uid);
          }
        }
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Request notification permission and store FCM token
  const registerFCMToken = useCallback(async (uid) => {
    try {
      const token = await requestNotificationToken();
      if (token) {
        // Store token in user's Firestore profile (array so multiple devices work)
        await updateDoc(doc(db, "users", uid), {
          fcmTokens: arrayUnion(token),
        });
        console.log("FCM token registered.");
      }
    } catch (err) {
      console.warn("FCM registration failed (non-critical):", err);
    }
  }, []);

  // Listen for foreground notifications (show in-app toast)
  useEffect(() => {
    const unsub = onForegroundMessage((payload) => {
      console.log("Foreground message:", payload);
      // Show a brief toast notification — the user is already in the app
      // so we don't need a full push notification, just a subtle alert
      if (payload.notification) {
        const { title, body } = payload.notification;
        // Use the browser Notification API for a quick toast
        if (Notification.permission === "granted") {
          new Notification(title || "CrewLink", {
            body: body || "New message",
            icon: "/icon-192.png",
            tag: "crewlink-foreground",
            silent: true, // Don't double-ding if they're in the chat
          });
        }
      }
    });
    return unsub;
  }, []);

  // Sign up — creates auth account + Firestore user profile
  const signUp = useCallback(
    async ({ email, password, username, displayName, preferredLanguage }) => {
      const cred = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const profile = {
        username: username.toLowerCase(),
        displayName,
        preferredLanguage: preferredLanguage || "en",
        photoURL: null,
        createdAt: serverTimestamp(),
      };
      await setDoc(doc(db, "users", cred.user.uid), profile);
      setUserProfile({ uid: cred.user.uid, ...profile });
      return cred.user;
    },
    []
  );

  // Sign in
  const signIn = useCallback(async (email, password) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    await firebaseSignOut(auth);
    setUser(null);
    setUserProfile(null);
  }, []);

  // Manual notification opt-in (needed for iOS user-gesture requirement)
  const requestNotifications = useCallback(async () => {
    if (!user) return false;
    const token = await requestNotificationToken();
    if (token) {
      await updateDoc(doc(db, "users", user.uid), {
        fcmTokens: arrayUnion(token),
      });
      console.log("FCM token registered via user gesture.");
      return true;
    }
    return false;
  }, [user]);

  // Check if notifications are already granted
  const notificationsEnabled =
    typeof Notification !== "undefined" && Notification.permission === "granted";

  return { user, userProfile, loading, signUp, signIn, signOut, requestNotifications, notificationsEnabled };
}
